import type { ReactNode } from 'react';
import { Icon, type IconName } from '../icons';
import { useUiT } from '../i18n';
import { cx } from './cx';
import '../styles/primitives/states.css';

export type BannerTone = 'info' | 'ok' | 'warn' | 'err' | 'neutral';

export type BannerProps = {
  tone?: BannerTone;
  title?: ReactNode;
  children?: ReactNode;
  icon?: IconName;
  /** A link-style action on the right ("Upgrade", "Reload"). */
  action?: ReactNode;
  /** Shows a dismiss button. */
  onDismiss?: () => void;
  className?: string;
};

const DEFAULT_ICON: Record<BannerTone, IconName> = {
  info: 'info',
  ok: 'check',
  warn: 'alert',
  err: 'alert',
  neutral: 'info',
};

/**
 * A full-width strip above page content (read-only access, offline, maintenance, a migration
 * notice). Warnings and errors are announced assertively, everything else politely.
 */
export function Banner({ tone = 'info', title, children, icon, action, onDismiss, className }: BannerProps) {
  const t = useUiT();
  return (
    <div
      className={cx('banner', tone, className)}
      role={tone === 'err' || tone === 'warn' ? 'alert' : 'status'}
    >
      <Icon name={icon ?? DEFAULT_ICON[tone]} size={16} className="banner-icon" />
      <div className="banner-text">
        {title ? <strong className="banner-title">{title}</strong> : null}
        {children ? <span>{children}</span> : null}
      </div>
      {action ? <span className="banner-action">{action}</span> : null}
      {onDismiss ? (
        <button type="button" className="banner-x" aria-label={t('ui.dismiss')} onClick={onDismiss}>
          <Icon name="x" size={14} />
        </button>
      ) : null}
    </div>
  );
}
